import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput, {
  type OutlinedInputProps,
} from '@mui/material/OutlinedInput';
import { formatNumber } from '@witmetrics/utils/strings';
import Box from '@/components/Box';
import { CaretDownIcon, CaretUpIcon } from '@/icons';
import type { NumberInputValue } from './types';
import { classes, getValue, incrementValue, styles } from './utils';

export type { NumberInputValue };

export type NumberInputProps = {
  disabled?: boolean;
  sx?: FormControlProps['sx'];
  className?: string;
  size?: 'small' | 'medium';
  name?: string;
  label: string;
  value: NumberInputValue;
  min?: number;
  max?: number;
  increment?: number;
  adornment?: ReactNode;
  onChange: (value: NumberInputValue) => void;
};

export default function NumberInput({
  disabled,
  sx = {},
  className,
  name = 'number',
  size = 'medium',
  label = 'Value',
  value,
  min,
  max,
  increment,
  adornment,
  onChange,
}: NumberInputProps) {
  const handleChange: OutlinedInputProps['onChange'] = (e) => {
    onChange(getValue(e.target.value, value, min, max));
  };

  const handleIncrement = (delta: 1 | -1) => {
    if (!increment) return console.warn('No increment provided');
    // formatNumber() returns a locale-formatted string
    onChange(incrementValue(value, increment * delta, min, max));
  };

  const inputId = `number-input_${name}`;

  return (
    <FormControl fullWidth disabled={disabled} sx={sx} className={className}>
      <InputLabel htmlFor={inputId}>{label}</InputLabel>
      <OutlinedInput
        sx={increment ? styles.incrementInput : {}}
        className={twMerge(increment && classes.incrementInput)}
        id={inputId}
        size={size}
        inputProps={{
          pattern: '[0-9\s,\.]*',
        }}
        placeholder={formatNumber(1234.56)}
        startAdornment={
          adornment ? (
            <InputAdornment position="start">{adornment}</InputAdornment>
          ) : undefined
        }
        endAdornment={
          increment ? (
            <Box
              sx={styles.incrementWrapper}
              className={classes.incrementWrapper}>
              <button
                className={classes.incrementButton}
                onClick={() => handleIncrement(1)}>
                <CaretUpIcon className={classes.incrementIcon} />
              </button>
              <button
                className={classes.incrementButton}
                onClick={() => handleIncrement(-1)}>
                <CaretDownIcon className={classes.incrementIcon} />
              </button>
            </Box>
          ) : undefined
        }
        label={label}
        value={value.label}
        onChange={handleChange}
      />
    </FormControl>
  );
}
